import PropTypes from 'prop-types'
import { Fragment } from 'react'
import { Link } from 'react-router-dom'

import agent from 'agent'
import { isKubernetesAttack } from 'components/attacks/utils/kubernetes'
import useAuth from 'hooks/use-auth'
import { track } from 'track'

import AlfiAttackRow from './AttackRows/AlfiAttackRow'
import IlfiAttackRow from './AttackRows/IlfiAttackRow'
import KubernetesAttackRow from './AttackRows/KubernetesAttackRow'
import ScenarioRow from './AttackRows/ScenarioRow'

export function haltApiRequest({
  attack,
  type,
  reason,
  runNumber,
  currentTeamId
}) {
  const { infraAttack = {} } = attack
  const hasInfraAttack = Boolean(infraAttack?.guid)
  track('ActiveAttackNotificationHalt')

  if (type === 'alfi') {
    return agent.delete(`/experiments/${attack.guid}?teamId=${currentTeamId}`)
  } else if (type === 'scenario') {
    return agent.post(
      `/scenarios/halt/${attack.guid}/runs/${runNumber}?teamId=${currentTeamId}`,
      { reason }
    )
  } else if (type === 'kubernetes') {
    return agent.post(
      hasInfraAttack
        ? `/attacks/${infraAttack.guid}/halt?teamId=${currentTeamId}`
        : `/kubernetes/attacks/${attack.uid}/halt?teamId=${currentTeamId}`,
      { reason }
    )
  } else {
    // includes type === "ilfi"
    return agent.post(`/attacks/${attack.guid}/halt?teamId=${currentTeamId}`, {
      reason
    })
  }
}

export default function ActiveAttacksList({
  ilfiAttacks,
  alfiAttacks,
  scenarios,
  alfiMetadata,
  doFetchIlfiAttacks,
  doFetchAlfiAttacks,
  doFetchScenarios,
  showHaltButton,
  clickableRows,
  selectableRows,
  onRowSelected,
  selectedRows,
  ...rest
}) {
  const { currentTeamId } = useAuth()
  return (
    <Fragment>
      {ilfiAttacks.map((attack) => {
        const AttackComponent = isKubernetesAttack(attack)
          ? KubernetesAttackRow
          : IlfiAttackRow
        const onHaltRequest = ({ reason } = {}) =>
          haltApiRequest({
            type: isKubernetesAttack(attack) ? 'kubernetes' : 'ilfi',
            attack,
            currentTeamId,
            reason
          })
        return (
          <AttackComponent
            key={attack.uid || attack.guid}
            attack={attack}
            checked={
              selectedRows[
                isKubernetesAttack(attack) ? attack.uid : attack.guid
              ]
            }
            clickableRow={clickableRows}
            doFetch={doFetchIlfiAttacks}
            selectableRow={selectableRows}
            showHaltButton={showHaltButton}
            onHaltAttack={onHaltRequest}
            onRowSelected={() =>
              onRowSelected({
                id: isKubernetesAttack(attack) ? attack.uid : attack.guid,
                onHaltRequest: onHaltRequest
              })
            }
            {...rest}
          />
        )
      })}
      {alfiAttacks.map((attack) => {
        const onHaltRequest = () =>
          haltApiRequest({
            type: 'alfi',
            attack,
            currentTeamId
          })
        return (
          <AlfiAttackRow
            key={attack.guid}
            as={clickableRows ? Link : 'div'}
            attack={attack}
            checked={selectedRows[attack.guid]}
            clickableRow={clickableRows}
            doFetch={doFetchAlfiAttacks}
            metadata={alfiMetadata}
            selectableRow={selectableRows}
            showHaltButton={showHaltButton}
            onHaltAttack={onHaltRequest}
            onRowSelected={() =>
              onRowSelected({
                id: attack.guid,
                onHaltRequest
              })
            }
            {...rest}
          />
        )
      })}
      {scenarios.map((scenario) => {
        const onHaltRequest = ({ reason, runNumber }) =>
          haltApiRequest({
            attack: scenario,
            type: 'scenario',
            currentTeamId,
            reason,
            runNumber
          })
        return (
          <ScenarioRow
            key={scenario.guid}
            as={clickableRows ? Link : 'div'}
            checked={selectedRows[scenario.guid]}
            clickableRow={clickableRows}
            doFetch={doFetchScenarios}
            scenario={scenario}
            selectableRow={selectableRows}
            showHaltButton={showHaltButton}
            onHaltAttack={onHaltRequest}
            onRowSelected={({ runNumber }) =>
              onRowSelected({
                id: scenario.guid,
                isScenario: true,
                runNumber,
                onHaltRequest
              })
            }
            {...rest}
          />
        )
      })}
    </Fragment>
  )
}

ActiveAttacksList.propTypes = {
  ilfiAttacks: PropTypes.array,
  alfiAttacks: PropTypes.array,
  scenarios: PropTypes.array,
  alfiMetadata: PropTypes.object,
  doFetchIlfiAttacks: PropTypes.func,
  doFetchAlfiAttacks: PropTypes.func,
  onRowSelected: PropTypes.func,
  doFetchScenarios: PropTypes.func,
  showHaltButton: PropTypes.bool,
  clickableRows: PropTypes.bool,
  selectableRows: PropTypes.bool,
  selectedRows: PropTypes.object
}

ActiveAttacksList.defaultProps = {
  onRowSelected: () => {},
  selectedRows: {}
}
